import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Buildings, MapPin, CurrencyDollar, CalendarBlank, FileText, SignOut, ArrowsLeftRight, Bell, Briefcase, ArrowLeft } from '@phosphor-icons/react';
import LocationSearch from '../components/LocationSearch';
import HelpButton from '../components/HelpButton';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthImage = ({ fileRef, alt, className }) => {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    if (!fileRef) return;
    const fileId = fileRef.includes('/') ? fileRef.split('/').pop().split('.')[0] : fileRef;
    let url = null;
    axios.get(`${API}/files/${fileId}/download`, { responseType: 'blob' })
      .then(res => { url = window.URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [fileRef]);
  if (!src) return <div className={className} style={{ background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Buildings size={48} weight="duotone" style={{ color: 'var(--text-muted)' }} /></div>;
  return <img src={src} alt={alt} className={className} />;
};

const JobsPage = () => {
  const { user, logout, adminView, setAdminView } = useContext(AuthContext);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setJobs(res.data.filter(p => ['open', 'bidding', 'awarded', 'in_progress'].includes(p.status)));
    } catch (err) { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  };

  const handleLocationSearch = async ({ location, radius }) => {
    if (!location) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/search/projects`, { params: { location, radius } });
      setSearchResults(res.data);
      if (res.data.length === 0) toast.info('No jobs found in this area');
    } catch (err) { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const displayJobs = searchResults !== null ? searchResults : jobs;

  if (loading) return (
    <div className="hb-bg min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-lg" style={{ color: 'var(--text-muted)' }}>Loading jobs...</div>
    </div>
  );

  return (
    <div className="hb-bg">
      <div style={{ width: '100%' }} className="min-h-screen">
        {/* Header */}
        <header className="hb-header sticky top-0 z-50">
          <div className="max-w-screen-2xl mx-auto px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/harperbids-logo.png" alt="HarperBids" className="h-10 hb-logo-glow" />
              <div>
                <h1 className="text-lg font-bold font-heading" style={{ color: 'var(--gold-light)' }}>HARPERBIDS</h1>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>All Jobs</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="back-to-dashboard"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{ background: 'rgba(201,206,211,0.1)', border: '1px solid rgba(201,206,211,0.2)', color: 'var(--gold)' }}
              >
                <ArrowLeft size={14} weight="bold" />
                Dashboard
              </button>
              {user.role === 'admin' && (
                <>
                  <button
                    data-testid="admin-switch-owner"
                    onClick={() => { setAdminView('owner'); navigate('/dashboard'); }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                    style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.15)', color: 'var(--gold)' }}
                  >
                    Owner View
                  </button>
                  <button
                    data-testid="admin-switch-gc"
                    onClick={() => { setAdminView('gc'); navigate('/dashboard'); }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                    style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.15)', color: 'var(--cyan)' }}
                  >
                    GC View
                  </button>
                </>
              )}
              <HelpButton />
              <div className="flex items-center gap-3 pl-3" style={{ borderLeft: '1px solid var(--border-primary)' }}>
                <div className="text-right">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.role === 'admin' ? 'Admin' : user.company || user.email}</p>
                </div>
                <Button data-testid="logout-button" onClick={logout} variant="ghost" size="sm" className="btn-ghost">
                  <SignOut size={18} />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content — wider container with generous spacing */}
        <main className="max-w-screen-2xl mx-auto px-8 pt-10 pb-16">
          {/* Page Title + Stats */}
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-4">
              <Briefcase size={32} weight="duotone" style={{ color: 'var(--gold)' }} />
              <div>
                <h1 className="text-3xl font-bold font-heading" style={{ color: 'var(--gold-light)' }}>All Jobs</h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{displayJobs.length} active job{displayJobs.length !== 1 ? 's' : ''} posted</p>
              </div>
            </div>
          </div>

          {/* Location Search */}
          <div className="mb-10" data-testid="jobs-location-search">
            <LocationSearch
              onSearch={handleLocationSearch}
              userLocation={user.location || ''}
              placeholder="Filter jobs by city, address, or ZIP..."
            />
          </div>

          {/* Jobs Grid */}
          {searching ? (
            <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Searching...</div>
          ) : displayJobs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" data-testid="jobs-grid">
              {displayJobs.map((job) => (
                <div
                  key={job.id}
                  data-testid={`job-card-${job.id}`}
                  onClick={() => navigate(`/projects/${job.id}`)}
                  className="hb-card overflow-hidden hb-lift cursor-pointer"
                >
                  {job.photos && job.photos.length > 0 ? (
                    <div className="h-52 overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
                      <AuthImage fileRef={job.photos[0]} alt={job.title} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="h-52 flex items-center justify-center" style={{ background: 'var(--bg-elevated)' }}>
                      <Buildings size={52} weight="duotone" style={{ color: 'var(--text-muted)' }} />
                    </div>
                  )}
                  <div className="p-7">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold font-heading line-clamp-2 leading-snug" style={{ color: 'var(--text-primary)' }}>{job.title}</h3>
                      {job.distance_miles !== null && job.distance_miles !== undefined && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium ml-3 shrink-0" style={{ background: 'rgba(201,206,211,0.1)', color: 'var(--gold)' }}>
                          {job.distance_miles} mi
                        </span>
                      )}
                    </div>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>Posted by {job.owner_name}</p>
                    <p className="text-sm mb-5 line-clamp-2 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{job.description}</p>
                    <div className="space-y-2.5 mb-5">
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><MapPin size={15} weight="fill" /> {job.location}</div>
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><CurrencyDollar size={15} /> {job.budget_range}</div>
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}><CalendarBlank size={15} /> Deadline: {new Date(job.deadline).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <span className={`hb-badge hb-badge-${job.status}`}>{job.status.replace('_', ' ')}</span>
                      <div className="flex items-center gap-3">
                        {job.documents && job.documents.length > 0 && (
                          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--gold)' }}>
                            <FileText size={13} weight="duotone" /> {job.documents.length}
                          </span>
                        )}
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{job.bid_count} bid{job.bid_count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="hb-empty p-16 text-center">
              <Briefcase size={64} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
              <h3 className="text-xl font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>
                {searchResults !== null ? 'No jobs found in this area' : 'No active jobs'}
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                {searchResults !== null ? 'Try a wider radius or different location' : 'Check back later for new opportunities'}
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default JobsPage;
